/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React from "react";
import {IconGuardTitle} from "../../../../../../components/buttons/IconButtons";
import {DATE_TIME_FORMAT_NAME_MODEL, getDateFormat} from "../../../../../../Apis/functions";
import GateLogCard from "../../EntryAndExitForms/Details/Sections/GateLogCard";
import GateLogGroup from "../../EntryAndExitForms/Details/Sections/GateLogGroup";
import GateLogValueBox from "../../EntryAndExitForms/Details/Sections/GateLogValueBox";
import TransportSection from "../../EntryAndExitForms/Details/Sections/TransportSection";
import RegisteredByChip from "../../EntryAndExitForms/Details/Sections/RegisteredByChip";
import {
    getFieldAudit,
    getGateLogEmptyText,
    getRegisteredByIcon,
    hasGateLogValue,
    MATERIAL_CONTROL_AUDIT_ENTITY,
    VEHICLE_MOVEMENT_DETAIL_MODEL,
    VEHICLE_MOVEMENT_TRANSPORT_ROWS
} from "../../../Model/Model";

/**
 * Gate log of a movement of a vehicle.
 *
 * The vehicle that makes the movement is read first, and then the moment it was
 * registered at the gate: the date and the time stay pending until the guard
 * closes the movement.
 *
 * @param {Function} t - Translation function.
 * @param {Object} item - Movement being read.
 * @param {Object} directionOption - Direction the movement takes.
 * @param {React.RefObject} contentRef - Reference of the scrollable body.
 */
const VehicleMovementGateLogSection = ({t, item, directionOption, contentRef}) => {
    // The moment of the movement is the one the folio declares, and a folio
    // registered without it is read with the moment the gate wrote when it
    // dispatched the movement, the same way every folio of material control
    // reads its date
    const movementEvent = VEHICLE_MOVEMENT_DETAIL_MODEL.getMovementEvent(item);
    // A movement the gate already registered is not waited for any more, so
    // what it was closed without is read as a value it does not carry
    const emptyText = getGateLogEmptyText(item, t);
    const registeredDate = item?.expectedMovementDate ?? movementEvent?.eventDate ?? null;
    // The date is written on the folio itself, so its history is the one that
    // names who wrote it: a date the gate completed carries the change that
    // registered it, and the one that never changed is the one the requester
    // asked for the movement with
    const movementDateAudit = getFieldAudit(
        item,
        MATERIAL_CONTROL_AUDIT_ENTITY.MATERIAL_CONTROL,
        'expectedMovementDate',
        item?.id
    );
    // A value of the vehicle is written by the requester when the folio travels
    // with it and by the guard when it was left blank, so the history of the
    // field is the one that names the guard and the request names the rest.
    // A value that is still blank has nobody to name, so it is read without a chip
    const requesterChip = (field, value) => {
        if (!hasGateLogValue(value)) return null;

        const audit = getFieldAudit(item, MATERIAL_CONTROL_AUDIT_ENTITY.TRANSPORT, field, item?.transport?.id);
        const name = audit?.changedBy || item?.requestedBy;

        return <RegisteredByChip
            Icon={getRegisteredByIcon(item, name)}
            name={name}
            date={audit?.changedAt ?? item?.createdAt}
        />;
    };

    /**
     * Chip the moment of the movement is read with.
     *
     * The date the requester declared is named by whoever wrote it on the
     * folio, and the one the gate stamped when it registered the movement is
     * named by the guard that wrote that movement.
     *
     * @returns {JSX.Element|null} Chip of the moment, null while it is pending.
     */
    const movementDateChip = () => {
        if (registeredDate === null) return null;

        if (item?.expectedMovementDate == null)
            return <RegisteredByChip
                Icon={IconGuardTitle}
                name={movementEvent?.registeredBy}
                date={movementEvent?.eventDate}
            />;

        const name = movementDateAudit?.changedBy || item?.requestedBy;

        return <RegisteredByChip
            Icon={getRegisteredByIcon(item, name)}
            name={name}
            date={movementDateAudit?.changedAt ?? item?.createdAt}
        />;
    };
    const dateChip = movementDateChip();

    // The notes of the movement are written on the folio itself, so their
    // history is the one that names who wrote them: the ones the gate completed
    // carry the change that registered them, and the ones that never changed
    // travelled with the request
    const notesAudit = getFieldAudit(item, MATERIAL_CONTROL_AUDIT_ENTITY.MATERIAL_CONTROL, 'movementNotes', item?.id);
    const hasNotes = hasGateLogValue(item?.movementNotes);
    const notesName = notesAudit?.changedBy || item?.requestedBy;
    const notesChip = hasNotes
        ? <RegisteredByChip
            Icon={notesAudit ? IconGuardTitle : getRegisteredByIcon(item, notesName)}
            name={notesName}
            date={notesAudit?.changedAt ?? item?.createdAt}
        />
        : null;

    return (
        <GateLogCard t={t} contentRef={contentRef}>
            <div className={"p-3"}>
                <TransportSection
                    t={t}
                    transport={item}
                    carrierTrans={'company_or_area'}
                    carrierField={'companyOrArea'}
                    rows={VEHICLE_MOVEMENT_TRANSPORT_ROWS}
                    carrierAction={requesterChip}
                    vehicleAction={requesterChip}
                    emptyText={emptyText}
                />

                {directionOption &&
                    <GateLogGroup
                        Icon={directionOption.gateIcon || directionOption.Icon}
                        title={t(directionOption.trans)}
                    >
                        <div className={"row"}>
                            <div className={"col-sm-12 col-md-6 px-1"}>
                                <GateLogValueBox
                                    label={t('date')}
                                    value={registeredDate
                                        ? getDateFormat(registeredDate, null, DATE_TIME_FORMAT_NAME_MODEL.slash)
                                        : emptyText}
                                    action={dateChip}
                                />
                            </div>
                            <div className={"col-sm-12 col-md-6 px-1"}>
                                <GateLogValueBox
                                    label={t('time')}
                                    value={registeredDate
                                        ? getDateFormat(registeredDate, null, DATE_TIME_FORMAT_NAME_MODEL.time12Uppercase)
                                        : emptyText}
                                    action={dateChip}
                                />
                            </div>
                            <div className={"col-12 px-1 mt-2"}>
                                <GateLogValueBox
                                    label={t('notes')}
                                    value={hasNotes ? item.movementNotes : emptyText}
                                    action={notesChip}
                                />
                            </div>
                        </div>
                    </GateLogGroup>
                }
            </div>
        </GateLogCard>
    );
};

export default VehicleMovementGateLogSection;
